import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  Plus,
  Code2,
  LayoutGrid,
  Puzzle,
  Search,
  Palette,
  Printer,
} from "lucide-react";

// 6 catégories de prestations
const categories = [
  {
    id: "conception",
    title: "Conception & développement",
    icon: Code2,
    items: [
      "Création ou refonte complète du site",
      "Site adapté aux mobiles, tablettes et ordinateurs",
      "Organisation et structure du site",
      "Design adapté à l'image de l'entreprise",
      "Intégration des textes, photos et contenus",
    ],
  },
  {
    id: "pages",
    title: "Création des pages",
    icon: LayoutGrid,
    items: [
      "Page d'accueil",
      "Présentation de l'entreprise",
      "Services ou prestations",
      "Tarifs",
      "Galerie / réalisations",
      "Informations pratiques",
      "Actualités ou événements",
      "FAQ",
      "Contact",
      "Réservation",
    ],
    footer:
      "Les pages sont définies en fonction de chaque activité et des besoins du projet.",
  },
  {
    id: "fonctionnalites",
    title: "Fonctionnalités & intégrations",
    icon: Puzzle,
    items: [
      "Formulaire de contact",
      "Réseaux sociaux",
      "Avis Google",
      "Google Maps et itinéraires",
      "Système de réservation ou prise de rendez-vous",
      "Galerie photos",
      "Boutons d'appel, email ou réservation",
      "Intégration d'outils ou logiciels externes",
    ],
  },
  {
    id: "visibilite",
    title: "Visibilité sur Google",
    icon: Search,
    items: [
      "Structure du site pensée pour être correctement comprise par Google",
      "Titres et descriptions des pages",
      "Optimisation des informations essentielles",
      "Aide à la visibilité locale",
      "Google Business",
      "Google Maps",
      "Mise en ligne et vérifications",
    ],
    footer:
      "Je mets en place les bonnes bases pour aider votre site à être visible et correctement référencé sur Google.",
  },
  {
    id: "identite",
    title: "Identité visuelle & photos",
    icon: Palette,
    items: [
      "Choix des couleurs et typographies",
      "Création ou adaptation de l'identité visuelle",
      "Création ou refonte de logo",
      "Mise en valeur des photos",
      "Recadrage et optimisation des images",
      "Galerie photos travaillée",
      "Harmonisation visuelle de l'ensemble du site",
    ],
  },
  {
    id: "supports",
    title: "Supports de communication",
    icon: Printer,
    items: [
      "Flyers",
      "Supports digitaux",
      "QR codes",
      "Déclinaisons graphiques",
      "Visuels simples pour la communication",
      "Autres supports selon les besoins",
    ],
  },
];

export default function Services() {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => {
    setOpenId((current) => (current === id ? null : id));
  };

  return (
    <div data-testid="services-page">
      {/* HERO — Positionnement */}
      <section className="container-up pt-20 sm:pt-24 md:pt-32 pb-10 md:pb-14">
        <div className="max-w-4xl mx-auto text-center reveal">
          <span className="section-label justify-center">Mon offre</span>
          <h1 className="mt-7 font-serif text-[2.25rem] sm:text-5xl md:text-6xl lg:text-[4rem] leading-[1.05] tracking-tight">
            Votre site internet,
            <em className="italic font-normal" style={{ color: "#DCC6A3" }}>
              {" "}
              pensé pour votre activité.
            </em>
          </h1>

          <p className="mt-8 text-up-text/90 text-lg md:text-xl leading-[1.75] max-w-2xl mx-auto">
            Pas de formule toute faite&nbsp;: chaque site est conçu selon
            votre activité, vos besoins et votre budget.
          </p>

          {/* Prix mis en avant */}
          <div
            className="mt-10 mx-auto max-w-md rounded-2xl px-7 py-8 ring-1"
            style={{
              background: "#F5EBD8",
              borderColor: "rgba(47,73,110,0.18)",
            }}
            data-testid="services-price-highlight"
          >
            <p className="text-[11px] tracking-[0.32em] uppercase text-up-brown">
              Les tarifs
            </p>
            <p className="mt-3 font-serif text-[2.75rem] sm:text-5xl text-up-brown tracking-tight leading-none">
              À partir de 600&nbsp;€&nbsp;HT
            </p>
            <p className="mt-3 font-serif italic text-up-brown/80 text-base">
              sur devis
            </p>
            <p className="mt-5 text-xs md:text-sm text-up-muted leading-relaxed">
              Budget généralement compris entre
              <strong className="text-up-brown"> 600 € et 2 500 € HT </strong>
              selon le projet et les prestations choisies.
            </p>
          </div>

          <p className="mt-10 text-up-text/90 text-base md:text-lg leading-[1.8] max-w-2xl mx-auto">
            Site vitrine, fonctionnalités spécifiques, identité visuelle, mise
            en ligne… Je construis avec vous une solution adaptée à ce dont
            vous avez réellement besoin.
          </p>
        </div>
      </section>

      {/* 6 CATÉGORIES INTERACTIVES */}
      <section
        className="relative border-y border-up-taupe/30 depth-soft-light"
        style={{ background: "#FBF6EC" }}
        data-testid="services-categories"
      >
        <div className="container-up py-20 md:py-28">
          <div className="max-w-3xl mx-auto text-center reveal mb-12 md:mb-16">
            <span className="section-label justify-center">Ce que je peux faire</span>
            <h2 className="mt-6 font-serif text-[1.75rem] sm:text-3xl md:text-[2.25rem] leading-[1.1] tracking-tight">
              Cliquez pour découvrir
              <em className="italic font-normal" style={{ color: "#DCC6A3" }}>
                {" "}
                les prestations.
              </em>
            </h2>
            <p className="mt-5 text-up-muted text-sm md:text-base leading-relaxed">
              Ces prestations sont des possibilités. Chaque projet est
              construit selon vos besoins réels.
            </p>
          </div>

          <div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-6xl mx-auto"
            data-testid="services-categories-grid"
          >
            {categories.map((cat, i) => {
              const Icon = cat.icon;
              const isOpen = openId === cat.id;
              return (
                <article
                  key={cat.id}
                  data-testid={`category-${cat.id}`}
                  className={`relative rounded-2xl bg-up-offwhite ring-1 transition-all duration-500 ${
                    isOpen
                      ? "ring-up-brown/40 shadow-[0_28px_60px_-25px_rgba(47,73,110,0.4)] sm:col-span-2 lg:col-span-3"
                      : "ring-up-taupe/40 hover:ring-up-brown/30 hover:shadow-[0_18px_40px_-18px_rgba(47,73,110,0.28)] hover:-translate-y-0.5"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggle(cat.id)}
                    aria-expanded={isOpen}
                    aria-controls={`${cat.id}-panel`}
                    data-testid={`category-${cat.id}-toggle`}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 md:px-7 md:py-6 text-left group"
                  >
                    <span className="flex items-center gap-4 min-w-0 flex-1">
                      <span
                        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-500"
                        style={{
                          background: isOpen ? "#2F496E" : "#DCC6A3",
                          color: isOpen ? "#F7F4EE" : "#2F496E",
                        }}
                        aria-hidden
                      >
                        <Icon className="h-5 w-5" strokeWidth={1.6} />
                      </span>
                      <span className="font-serif text-base md:text-[1.1rem] text-up-brown tracking-tight leading-[1.2]">
                        {cat.title}
                      </span>
                    </span>
                    <span
                      aria-hidden
                      className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1 transition-all duration-500 ${
                        isOpen
                          ? "bg-up-brown text-up-sand ring-up-brown/40 rotate-45"
                          : "bg-up-sand/40 text-up-brown ring-up-brown/20 group-hover:bg-up-brown group-hover:text-up-sand"
                      }`}
                    >
                      <Plus className="h-4 w-4" strokeWidth={2} />
                    </span>
                  </button>

                  {/* Panel déroulant */}
                  <div
                    id={`${cat.id}-panel`}
                    className={`grid overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className="px-6 pb-7 md:px-7 md:pb-8 pt-1 border-t border-up-taupe/30">
                        <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                          {cat.items.map((item) => (
                            <li
                              key={item}
                              className="flex items-start gap-3 text-up-text text-[14px] md:text-[15px] leading-[1.6]"
                            >
                              <CheckCircle2
                                className="h-4 w-4 mt-1 shrink-0 text-up-brown"
                                strokeWidth={1.6}
                              />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        {cat.footer && (
                          <p
                            className="mt-6 font-serif italic text-[14px] md:text-[15px] leading-[1.75] rounded-xl ring-1 px-4 py-3"
                            style={{
                              color: "#2F496E",
                              background: "rgba(220,198,163,0.2)",
                              borderColor: "rgba(47,73,110,0.15)",
                            }}
                          >
                            {cat.footer}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPLICATION DU PRIX */}
      <section
        className="container-up py-20 md:py-28"
        data-testid="services-price-explain"
      >
        <div className="max-w-3xl mx-auto reveal">
          <div className="text-center">
            <span className="section-label justify-center">Les tarifs</span>
            <h2 className="mt-6 font-serif text-[1.75rem] sm:text-3xl md:text-[2.25rem] leading-[1.1] tracking-tight">
              Un tarif adapté à
              <em className="italic font-normal" style={{ color: "#DCC6A3" }}>
                {" "}
                votre projet.
              </em>
            </h2>
            <div aria-hidden className="mx-auto mt-8 h-px w-12 bg-up-brown/40" />
          </div>

          <p className="mt-10 text-up-text/90 text-base md:text-lg leading-[1.9] text-center">
            Un site simple n&apos;a pas le même coût qu&apos;un projet
            comprenant plusieurs pages, un système de réservation, une
            identité visuelle ou des supports de communication. C&apos;est
            pourquoi chaque projet fait l&apos;objet d&apos;un devis
            personnalisé.
          </p>

          <div
            className="mt-10 mx-auto max-w-xl rounded-2xl px-7 py-10 text-center ring-1"
            style={{
              background: "#F5EBD8",
              borderColor: "rgba(47,73,110,0.18)",
            }}
          >
            <p className="font-serif text-[2.75rem] sm:text-5xl md:text-6xl text-up-brown tracking-tight leading-none">
              À partir de 600&nbsp;€&nbsp;HT
            </p>
            <p className="mt-6 text-sm md:text-base text-up-brown/85 leading-relaxed">
              La majorité des projets se situent entre{" "}
              <strong className="text-up-brown font-semibold">
                600 € et 2 500 € HT
              </strong>{" "}
              selon les besoins.
            </p>
          </div>

          <p className="mt-10 font-serif italic text-up-brown text-lg md:text-xl text-center leading-[1.7]">
            Vous choisissez ce dont vous avez besoin, je construis la
            prestation autour de votre projet.
          </p>

          <p className="mt-8 text-center text-[13px] md:text-sm text-up-muted leading-relaxed max-w-lg mx-auto">
            Un accompagnement personnalisé et des tarifs plus accessibles
            qu&apos;une agence traditionnelle.
          </p>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="container-up pt-4 pb-24 md:pb-32">
        <div className="relative overflow-hidden rounded-3xl bg-up-brown text-up-offwhite px-6 sm:px-10 md:px-20 py-20 sm:py-24 md:py-28 text-center">
          <div className="absolute inset-0 grain" />
          <div
            className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-up-sand/10 blur-3xl"
            aria-hidden
          />
          <div
            className="absolute -bottom-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-up-sand/5 blur-3xl"
            aria-hidden
          />
          <div className="relative z-10 max-w-2xl mx-auto reveal">
            <span className="inline-block text-[11px] sm:text-xs tracking-[0.32em] uppercase font-medium text-up-sand/90">
              On en parle ?
            </span>
            <h2 className="mt-6 font-serif text-[2rem] sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.1] tracking-tight !text-up-offwhite">
              Parlons de
              <em className="italic font-normal text-up-sand"> votre projet.</em>
            </h2>

            <div aria-hidden className="mx-auto mt-8 h-px w-12 bg-up-sand/50" />

            <p className="mt-10 text-up-sand leading-[1.85] text-base sm:text-lg max-w-xl mx-auto">
              Expliquez-moi votre projet et vos besoins. Nous définissons
              ensemble la solution la plus adaptée à votre activité et à votre
              budget.
            </p>

            <Link
              to="/contact"
              className="mt-12 inline-flex items-center gap-3 rounded-full bg-up-sand text-up-brown px-9 sm:px-11 py-4 sm:py-5 text-[15px] sm:text-base font-medium tracking-wide transition-all duration-500 hover:-translate-y-0.5 hover:bg-up-offwhite hover:shadow-[0_24px_50px_-20px_rgba(220,198,163,0.6)]"
              data-testid="services-cta-devis"
            >
              Demander mon devis personnalisé
              <ArrowUpRight className="h-5 w-5" />
            </Link>

            <p className="mt-6 text-xs sm:text-sm text-up-sand/70 italic">
              Premier échange gratuit · sans engagement
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
